/**
 * 二重・目元カテゴリ（二重埋没法）の正式コンテンツデータ。
 * NOTE: 埋没法の具体的な固定方法・糸の商品名・施術時間・麻酔方法・ダウンタイム日数・抜糸の有無・
 *       持続期間・症例数は未確認のため作成しない。未確定の項目は行自体を表示しない。
 *       「永久」「取れない」「腫れない」「必ず自然」「バレない」などの断定表現は使用しない。
 *       料金は公式サイトの定価（1回）を掲載する。モニター価格は掲載しない。
 */

export type EyeSpec = { label: string; value: string };
export type EyePriceRow = { label: string; price: string };
export type EyeFeature = { no: string; title: string; text: string };
export type EyeStep = { no: string; title: string; text: string };
export type EyeFaq = { q: string; a: string };
export type EyeRelated = { label: string; to: string };

export type EyeDetail = {
  slug: "double-eyelid";
  path: string;
  priceTab: string;
  en: string;
  jp: string;
  heroMain: string;
  heroSub: string;
  heroImageAlt: string;
  /** FV画像URL（正式画像が用意されたため差し込む） */
  heroImage: string;
  /** FV画像のobject-position */
  heroObjectPosition: string;
  heroNote: string;
  aboutJp: string;
  aboutParagraphs: string[];
  recommendedJp: string;
  recommended: string[];
  featuresJp: string;
  features: EyeFeature[];
  /** 施術情報（METHOD）。分類・施術方法の2項目をストリップ形式で表示する。 */
  method: EyeSpec[];
  /** 公式サイトの定価（1回）。 */
  price: EyePriceRow[];
  flow: EyeStep[];
  downtimeJp: string;
  downtime: EyeSpec[];
  riskJp: string;
  riskParagraphs: string[];
  faqs: EyeFaq[];
  related: EyeRelated[];
  ctaHeading: string;
  ctaLineLabel: string;
};

const flow: EyeStep[] = [
  { no: "01", title: "ご予約", text: "WEB・LINE・電話からご予約ください。" },
  { no: "02", title: "カウンセリング", text: "目元のお悩みやご希望を確認します。" },
  { no: "03", title: "医師の診察", text: "まぶたの状態を確認し、治療の適応を判断します。" },
  { no: "04", title: "治療内容・料金の説明", text: "治療内容、料金、リスク、注意事項をご説明します。" },
  { no: "05", title: "施術", text: "内容に同意いただいたうえで施術を行います。" },
  { no: "06", title: "アフターケア", text: "施術後の注意事項をご案内します。" },
];

/** 未確定のダウンタイムは行自体を非表示にする（ダミー値「—」の大量表示を避ける）。 */

export const eyeDetails: EyeDetail[] = [
  {
    slug: "double-eyelid",
    path: "/eye/double-eyelid",
    priceTab: "eye",
    en: "DOUBLE EYELID",
    jp: "二重埋没法",
    heroMain: "医療用の糸を用いて、二重のラインを形成する施術。",
    heroSub: "まぶたの状態やご希望を確認し、医師の診察のもと治療内容をご提案します。",
    heroImageAlt: "渋谷美容皮膚科クリニックの二重埋没法",
    heroImage:
      "/images/site/sbdc-double-eyelid-fv-v1.webp",
    heroObjectPosition: "object-center",
    heroNote: "",
    aboutJp: "二重埋没法とは",
    aboutParagraphs: [
      "二重埋没法は、医療用の糸を用いて二重のラインを形成する施術です。",
      "SBDCでは、まぶたの状態やご希望を確認し、医師の診察のもと治療の適応を判断します。",
    ],
    recommendedJp: "このようなお悩みの方へ",
    recommended: [
      "二重にしたい",
      "目元を整えたい",
      "埋没法が気になる",
      "自然な仕上がりを希望したい",
      "ダウンタイムが気になる",
    ],
    featuresJp: "特徴",
    features: [
      { no: "01", title: "まぶたの状態を確認", text: "まぶたの厚みや状態などを確認し、治療についてご相談いただけます。" },
      { no: "02", title: "ご希望の二重ラインについて相談", text: "ご希望や目元の印象について確認しながら、治療内容を検討します。" },
      { no: "03", title: "医師の診察のもと治療", text: "医師の診察のもと、治療の適応を判断します。" },
      { no: "04", title: "治療内容を丁寧に説明", text: "治療内容、リスク、注意事項について事前にご説明します。" },
    ],
    method: [
      { label: "分類", value: "EYE TREATMENT" },
      { label: "施術方法", value: "二重埋没法" },
    ],
    price: [
      { label: "二重埋没法 2点留め（保証なし）", price: "99,000円" },
      { label: "二重埋没法 2点留め（1年保証）", price: "148,500円" },
    ],
    flow,
    downtimeJp: "ダウンタイム",
    downtime: [],
    riskJp: "リスク・注意事項",
    riskParagraphs: [
      "施術後に、腫れ、内出血、痛みなどが生じる場合があります。",
      "また、二重幅の変化、左右差、感染などが生じる可能性があります。",
      "治療に伴うリスク、施術を受けられない場合、術後の注意事項については診察時に医師よりご説明します。",
      "症状や経過には個人差があります。",
    ],
    faqs: [
      {
        q: "二重埋没法はどのような施術ですか？",
        a: "医療用の糸を用いて二重のラインを形成する施術です。具体的な術式は診察時にご説明します。",
      },
      {
        q: "どのような二重にできますか？",
        a: "まぶたの状態やご希望を確認し、医師が適応を判断したうえで治療内容をご提案します。",
      },
      {
        q: "ダウンタイムはありますか？",
        a: "腫れや内出血などが生じる場合があります。",
      },
      {
        q: "保証はありますか？",
        a: "保証なし（99,000円）と、1年保証（148,500円）の2種類をご用意しています。",
      },
      {
        q: "料金はいくらですか？",
        a: "2点留め・保証なしは99,000円、2点留め・1年保証は148,500円です（税込）。",
      },
    ],
    related: [
      { label: "二重・目元へ戻る", to: "/treatment/eye" },
      { label: "料金表を見る", to: "/price?tab=eye" },
    ],
    ctaHeading: "二重埋没法について、まずはご相談ください。",
    ctaLineLabel: "二重埋没法についてLINEで相談",
  },
];

export function getEyeDetail(slug: string) {
  return eyeDetails.find((page) => page.slug === slug);
}